import { COMPANY_INFO } from '@/lib/constants';

interface CalculatorState {
  projectDurationMonths: number;
  softwareType: string;
  expectedUsers: number;
  avgUsageHoursPerWeek: number;
  userRegion: string;
  devRegion: string;
  infraRegion: string;
  projectLifespanYears: number;
}

interface CalculatorResults {
  totalEnergy_kWh: number;
  totalCO2_kg: number;
  flightsParisToNY: number;
  breakdown: {
    devEnergy_kWh: number;
    devCO2_kg: number;
    infraEnergy_kWh: number;
    infraCO2_kg: number;
    userEnergy_kWh: number;
    userCO2_kg: number;
  };
}

const formatNumber = (num: number, decimals: number = 2) => {
  return num.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

export const generateCarbonReport = (
  state: CalculatorState,
  results: CalculatorResults
): string => {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          
          body {
            font-family: 'Inter', sans-serif;
            margin: 0;
            padding: 0;
            color: #1e293b;
          }
          
          .page {
            width: 21cm;
            height: 29.7cm;
            padding: 2cm;
            position: relative;
            box-sizing: border-box;
          }
          
          .header {
            background: linear-gradient(135deg, #059669 0%, #047857 100%);
            color: white;
            padding: 1.5cm 2cm;
            margin: -2cm -2cm 1cm -2cm;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          
          .header-content {
            flex: 1;
          }
          
          .logo {
            width: 3cm;
            height: auto;
            margin-right: 1cm;
          }
          
          .title {
            font-size: 24pt;
            font-weight: 700;
            margin: 0;
          }
          
          .date {
            font-size: 10pt;
            opacity: 0.8;
            margin-top: 0.5cm;
          }
          
          .section {
            margin-bottom: 1cm;
          }
          
          .section-title {
            font-size: 16pt;
            color: #059669;
            margin-bottom: 0.5cm;
            border-bottom: 2px solid #059669;
            padding-bottom: 0.2cm;
          }
          
          .grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.5cm;
          }
          
          .item {
            display: flex;
            justify-content: space-between;
            padding: 0.3cm 0;
            border-bottom: 1px solid #e2e8f0;
          }
          
          .label {
            font-weight: 500;
            color: #64748b;
          }
          
          .value {
            font-weight: 600;
          }
          
          .summary-box, .breakdown-box {
            background-color: #f0fdf4;
            border: 1px solid #86efac;
            border-radius: 0.3cm;
            padding: 0.5cm;
            margin: 0.5cm 0;
          }
          
          .breakdown-title {
            font-weight: 600;
            color: #059669;
            margin-bottom: 0.3cm;
          }
          
          .footer {
            position: absolute;
            bottom: 1cm;
            left: 2cm;
            right: 2cm;
            text-align: center;
            font-size: 8pt;
            color: #64748b;
            border-top: 1px solid #e2e8f0;
            padding-top: 0.3cm;
          }
          
          .company-section {
            padding: 1cm;
            background-color: #f8fafc;
            border-radius: 0.3cm;
            border: 1px solid #e2e8f0;
          }
          
          .company-title {
            font-size: 18pt;
            color: #059669;
            margin-bottom: 0.5cm;
          }
          
          .company-description {
            margin-bottom: 0.5cm;
            line-height: 1.6;
          }
          
          .services-list {
            list-style-type: none;
            padding: 0;
            margin: 0;
          }
          
          .service-item {
            display: flex;
            align-items: center;
            margin-bottom: 0.3cm;
          }
          
          .service-icon {
            color: #059669;
            margin-right: 0.3cm;
          }

          .contact-info {
            margin-top: 1cm;
            padding-top: 1cm;
            border-top: 1px solid #e2e8f0;
          }

          .contact-item {
            display: flex;
            align-items: center;
            margin-bottom: 0.5cm;
          }

          .contact-icon {
            color: #059669;
            margin-right: 0.5cm;
            font-size: 1.2em;
          }

          .booking-link {
            display: inline-block;
            margin-top: 1cm;
            color: #059669;
            text-decoration: underline;
            text-underline-offset: 0.4em;
            font-weight: 600;
            font-size: 0.9em;
            line-height: 1.5;
          }
        </style>
      </head>
      <body>
        <!-- Page 1 -->
        <div class="page">
          <div class="header">
            <img src="icon.png" alt="Orcsys Logo" class="logo" />
            <div class="header-content">
              <h1 class="title">Rapport d'Empreinte Carbone</h1>
              <div class="date">Généré le ${new Date().toLocaleDateString('fr-FR')}</div>
            </div>
          </div>
          
          <div class="section">
            <h2 class="section-title">Détails du Projet</h2>
            <div class="grid">
              <div class="item">
                <span class="label">Type de Logiciel</span>
                <span class="value">${state.softwareType}</span>
              </div>
              <div class="item">
                <span class="label">Durée du Projet</span>
                <span class="value">${state.projectDurationMonths} mois</span>
              </div>
              <div class="item">
                <span class="label">Nombre d'Utilisateurs</span>
                <span class="value">${formatNumber(state.expectedUsers, 0)}</span>
              </div>
              <div class="item">
                <span class="label">Utilisation Moyenne</span>
                <span class="value">${state.avgUsageHoursPerWeek} h/semaine</span>
              </div>
              <div class="item">
                <span class="label">Durée de Vie</span>
                <span class="value">${state.projectLifespanYears} ans</span>
              </div>
            </div>
          </div>
          
          <div class="section">
            <h2 class="section-title">Localisations</h2>
            <div class="grid">
              <div class="item">
                <span class="label">Équipe de Développement</span>
                <span class="value">${state.devRegion}</span>
              </div>
              <div class="item">
                <span class="label">Infrastructure</span>
                <span class="value">${state.infraRegion}</span>
              </div>
              <div class="item">
                <span class="label">Utilisateurs</span>
                <span class="value">${state.userRegion}</span>
              </div>
            </div>
          </div>
          
          <div class="section">
            <h2 class="section-title">Résumé</h2>
            <div class="summary-box">
              <div class="item">
                <span class="label">Consommation Énergétique Totale</span>
                <span class="value">${formatNumber(results.totalEnergy_kWh, 2)} kWh</span>
              </div>
              <div class="item">
                <span class="label">Émissions de CO₂ Totales</span>
                <span class="value">${(results.totalCO2_kg / 1000).toFixed(5)} tonnes</span>
              </div>
              <div class="item">
                <span class="label">Équivalent Vols Paris-NY</span>
                <span class="value">${results.flightsParisToNY.toFixed(1)} vols</span>
              </div>
            </div>
          </div>
          
          <div class="footer">
            © Orcsys - Calculateur d'Empreinte Carbone
          </div>
        </div>

        <!-- Page 2 -->
        <div class="page">
          <div class="section">
            <h2 class="section-title">Détail des Émissions</h2>
            
            <div class="breakdown-box">
              <div class="breakdown-title">Développement</div>
              <div class="item">
                <span class="label">Énergie</span>
                <span class="value">${formatNumber(results.breakdown.devEnergy_kWh, 2)} kWh</span>
              </div>
              <div class="item">
                <span class="label">CO₂</span>
                <span class="value">${(results.breakdown.devCO2_kg / 1000).toFixed(5)} tonnes</span>
              </div>
            </div>
            
            <div class="breakdown-box">
              <div class="breakdown-title">Infrastructure</div>
              <div class="item">
                <span class="label">Énergie</span>
                <span class="value">${formatNumber(results.breakdown.infraEnergy_kWh, 2)} kWh</span>
              </div>
              <div class="item">
                <span class="label">CO₂</span>
                <span class="value">${(results.breakdown.infraCO2_kg / 1000).toFixed(5)} tonnes</span>
              </div>
            </div>
            
            <div class="breakdown-box">
              <div class="breakdown-title">Utilisateurs</div>
              <div class="item">
                <span class="label">Énergie</span>
                <span class="value">${formatNumber(results.breakdown.userEnergy_kWh, 2)} kWh</span>
              </div>
              <div class="item">
                <span class="label">CO₂</span>
                <span class="value">${(results.breakdown.userCO2_kg / 1000).toFixed(5)} tonnes</span>
              </div>
            </div>
          </div>
          
          <div class="section">
            <h2 class="section-title">Compensation Carbone</h2>
            <div class="summary-box">
              <div class="item">
                <span class="label">Arbres nécessaires (5 ans)</span>
                <span class="value">${(results.totalCO2_kg / (5 * 21)).toFixed(0)} arbres</span>
              </div>
              <div class="item">
                <span class="label">Arbres nécessaires (10 ans)</span>
                <span class="value">${(results.totalCO2_kg / (10 * 21)).toFixed(0)} arbres</span>
              </div>
              <div class="item">
                <span class="label">Arbres nécessaires (20 ans)</span>
                <span class="value">${(results.totalCO2_kg / (20 * 21)).toFixed(0)} arbres</span>
              </div>
            </div>
            <p style="font-size: 10pt; color: #64748b; margin-top: 0.5cm;">
              * Un arbre mature absorbe en moyenne 21 kg de CO₂ par an. Les valeurs sont calculées sur la base de cette moyenne.
            </p>
          </div>
          
          <div class="footer">
            © Orcsys - Calculateur d'Empreinte Carbone
          </div>
        </div>

        <!-- Page 3 -->
        <div class="page">
          <div class="company-section">
            <h2 class="company-title">À Propos d'Orcsys</h2>
            <p class="company-description">
              Orcsys accompagne les entrepreneurs et startups dans leur transformation digitale. Nous développons des solutions numériques innovantes, des applications mobiles aux plateformes web, en mettant l'accent sur la performance et l'impact environnemental.
            </p>
            
            <h3 class="section-title">Nos Services</h3>
            <ul class="services-list">
              <li class="service-item">
                <span class="service-icon">✓</span>
                Développement d'applications SaaS
              </li>
              <li class="service-item">
                <span class="service-icon">✓</span>
                Applications mobiles natives et cross-platform
              </li>
              <li class="service-item">
                <span class="service-icon">✓</span>
                Automatisation de processus métier
              </li>
              <li class="service-item">
                <span class="service-icon">✓</span>
                Marketplaces et plateformes web
              </li>
            </ul>
            
            <p class="company-description">
              Notre approche agile nous permet de développer rapidement des MVP fonctionnels, puis d'itérer pour créer des solutions robustes et performantes. Nous utilisons les technologies les plus modernes (React, Next.js, TypeScript, Django, Node.js) et assurons un support complet après le lancement.
            </p>

            <div class="contact-info">
              <div class="contact-item">
                <span class="contact-icon">📧</span>
                <span>${COMPANY_INFO.email}</span>
              </div>
              <div class="contact-item">
                <span class="contact-icon">📱</span>
                <span>${COMPANY_INFO.phone}</span>
              </div>
              <div class="contact-item">
                <span class="contact-icon">🌐</span>
                <span>${COMPANY_INFO.website}</span>
              </div>
            </div>
          </div>
          
          <div class="footer">
            © Orcsys - Calculateur d'Empreinte Carbone
          </div>
        </div>
      </body>
    </html>
  `;
};
